/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

// Copyright 2019 Schibsted
/**
 * Takes an action type name, a reducer, and optionally a custom action creator, and returns an
 * action creator function with the type and reducer attached.
 *
 * An map of action creators can be passed to [makeReducer()]{@link makeReducer} to automatically
 * generate a reducer that handles all the provided actions.
 *
 * The reducer argument can be either a function or a string:
 *  - If a function is provided, it should have the normal reducer signature:
 *    (state, action) => newState The function should return a new state object and should _not_
 *    modify the state argument.
 *  - If a string is provided instead, a simple single-value reducer will be generated
 *    automatically. The automatic reducer returns a new state where the property with the provided
 *    name is set to the value of the payload property on the action object.
 *
 * The default action creator function will return an action that takes one optional argument: The
 * value of the `payload` field.
 *
 * @param {String} type The action type label
 * @param {Function|String} reducer A reducer function, or the name of a field in the state that
 *        this action will affect
 * @param {Function} [creator] An action creation function
 * @return {Function} An action creation function
 * @see {@link makeReducer}
 */
function makeAction(type, reducer, creator) {
    if (type === undefined || reducer === undefined) {
        throw new Error("makeAction requires a type and a reducer");
    }
    var reducerFn = typeof reducer === "string" ? defaultReducer.bind(null, type, reducer) : reducer;
    var creatorFn = typeof creator === "function" ? creator : defaultCreator.bind(null, type);
    return Object.assign(creatorFn.bind(null), { type: type, reducer: reducerFn });
}
function defaultReducer(type, field, state, action) {
    var _a;
    if (action.type === type) {
        return __assign(__assign({}, state), (_a = {}, _a[field] = action.payload, _a));
    }
    else {
        return state;
    }
}
function defaultCreator(type, payload) {
    return { type: type, payload: payload };
}
/**
 * Takes an object consisting of action definitions as defined by [makeAction()]{@link makeAction},
 * and returns a reducer function that handles all the provided actions.

 * @param {ActionsObject} actions An object where the values are actions made with
 *        [makeAction()]{@link makeAction}
 * @returns {ReduxReducer}
 * @see {@link makeAction}
 */
function makeReducer(actions) {
    if (typeof actions !== "object") {
        throw new Error("Failed to make reducer: No actions specified");
    }
    var reducerActions = Object.keys(actions)
        .filter(function (name) { return !!actions[name].reducer && !!actions[name].type; })
        .reduce(function (last, name) {
        var _a;
        return (__assign(__assign({}, last), (_a = {}, _a[name] = actions[name], _a)));
    }, {});
    function reducer(state, action) {
        if (!state || typeof state !== "object") {
            throw new Error("Failed to execute reducer: Missing state");
        }
        else if (!action || typeof action !== "object" || !action.type) {
            throw new Error("Failed to execute reducer: Missing or invalid action");
        }
        var actionNames = Object.keys(reducerActions).filter(function (actionName) { return actions[actionName].type === action.type; });
        if (actionNames.length === 1) {
            return reducerActions[actionNames[0]].reducer(state, action);
        }
        else if (actionNames.length === 0) {
            return state;
        }
        else {
            throw new Error("Cannot execute reducer for action type \"" + action.type + "\" - multiple matching reducers found");
        }
    }
    reducer.actions = Object.keys(reducerActions);
    return reducer;
}

export { makeAction, makeReducer };
